import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, Plus, Trash2, Wrench, Cog, Palette, Users, Globe } from 'lucide-react';
import {
  createListing, updateListing, getListing, publishListing,
  getVerticalCategories, getMachineManufacturers, getMachineModels,
  uploadListingDocument,
} from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ListingPhotoUploader } from '@/components/ListingPhotoUploader';

const CONDITION_OPTIONS = [
  { value: 'new', label: 'Neu' },
  { value: 'refurbished', label: 'Generalüberholt' },
  { value: 'used_excellent', label: 'Gebraucht – sehr gut' },
  { value: 'used_good', label: 'Gebraucht – gut' },
  { value: 'used_fair', label: 'Gebraucht – akzeptabel' },
  { value: 'for_parts', label: 'Für Ersatzteile' },
];

const initialState = {
  vertical: 'spare_parts',
  title: '', description: '',
  category_node_id: '', condition: 'used_good',
  price_eur: '', currency: 'EUR', negotiable: true,
  country: 'DE', city: '', postal_code: '',
  photos: [], documents: [], video_links: [],
  visibility: 'public',
  // spare parts
  part_number: '', oem_number: '', quantity: '', unit: 'pcs',
  compatible_machines: [],
  // machines
  manufacturer_id: '', manufacturer_name: '', model_name: '',
  year_of_manufacture: '', serial_number: '',
  specs: { max_width_mm: '', num_colors: '', max_speed_mh: '', substrate_types: [], power_kw: '', weight_kg: '', dimensions_lwh_mm: '' },
  inspection_available: false, operating_hours: '',
  // paints REACH/CLP
  best_before_date: '',
  safety_data_sheet_url: '', safety_data_sheet_name: '',
  ufi_code: '', hazard_pictograms: [], hazard_statements: '', signal_word: 'none',
};

export default function CreateEditListingPage() {
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!listingId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({ ...initialState, vertical: searchParams.get('vertical') || 'spare_parts' });
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [videoInput, setVideoInput] = useState('');

  useEffect(() => {
    (async () => {
      if (isEditMode) {
        try {
          const res = await getListing(listingId);
          const l = res.data;
          setForm({
            ...initialState,
            ...l,
            price_eur: l.price_eur ?? '',
            year_of_manufacture: l.year_of_manufacture ?? '',
            operating_hours: l.operating_hours ?? '',
            quantity: l.quantity ?? '',
            specs: { ...initialState.specs, ...(l.specs || {}) },
            compatible_machines: l.compatible_machines || [],
          });
        } catch (e) { toast.error('Inserat konnte nicht geladen werden'); }
        finally { setLoading(false); }
      }
    })();
  }, [isEditMode, listingId]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, mfrs] = await Promise.all([
          getVerticalCategories(form.vertical),
          getMachineManufacturers({ active_only: true }),
        ]);
        setCategories(cats.data || []);
        setManufacturers(mfrs.data || []);
      } catch { /* ignore */ }
    })();
  }, [form.vertical]);

  useEffect(() => {
    if (!form.manufacturer_id) { setModels([]); return; }
    getMachineModels({ manufacturer_id: form.manufacturer_id }).then(r => setModels(r.data || [])).catch(() => setModels([]));
  }, [form.manufacturer_id]);

  const handleSave = async (publish = false) => {
    if (!form.title.trim()) { toast.error('Titel fehlt'); return; }
    if (!form.category_node_id) { toast.error('Kategorie fehlt'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price_eur: form.price_eur === '' ? null : Number(form.price_eur),
        quantity: form.quantity === '' ? null : Number(form.quantity),
        year_of_manufacture: form.year_of_manufacture === '' ? null : Number(form.year_of_manufacture),
        operating_hours: form.operating_hours === '' ? null : Number(form.operating_hours),
        specs: form.vertical === 'machines' ? {
          ...form.specs,
          max_width_mm: form.specs?.max_width_mm === '' ? null : Number(form.specs?.max_width_mm),
          num_colors: form.specs?.num_colors === '' ? null : Number(form.specs?.num_colors),
          max_speed_mh: form.specs?.max_speed_mh === '' ? null : Number(form.specs?.max_speed_mh),
          power_kw: form.specs?.power_kw === '' ? null : Number(form.specs?.power_kw),
          weight_kg: form.specs?.weight_kg === '' ? null : Number(form.specs?.weight_kg),
        } : null,
        status: publish ? 'active' : (form.status === 'active' ? 'active' : 'draft'),
      };

      let res;
      if (isEditMode) {
        res = await updateListing(listingId, payload);
        if (publish && form.status !== 'active') {
          await publishListing(listingId);
        }
        toast.success(publish ? 'Inserat veröffentlicht' : 'Inserat gespeichert');
      } else {
        res = await createListing(payload);
        toast.success(publish ? 'Inserat veröffentlicht' : 'Als Entwurf gespeichert');
      }
      navigate(`/listing/${res.data.listing_id || listingId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const addCompatibleMachine = () => {
    setForm(f => ({ ...f, compatible_machines: [...(f.compatible_machines || []), { manufacturer_id: '', manufacturer_name: '', model_name: '' }] }));
  };
  const updateCompatible = (idx, patch) => {
    setForm(f => ({ ...f, compatible_machines: f.compatible_machines.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  };
  const removeCompatible = (idx) => {
    setForm(f => ({ ...f, compatible_machines: f.compatible_machines.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return <div className="min-h-screen bg-background"><Header /><div className="p-12 text-center text-muted-foreground">Laden…</div></div>;
  }

  const isMachines = form.vertical === 'machines';
  const isSpareParts = form.vertical === 'spare_parts';
  const isPaints = form.vertical === 'paints';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl" data-testid="create-edit-listing-page">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
        </Button>
        <div className="flex items-center gap-3 mb-6">
          {isMachines ? <Cog className="w-8 h-8 text-blue-500" /> : isPaints ? <Palette className="w-8 h-8 text-fuchsia-500" /> : <Wrench className="w-8 h-8 text-orange-500" />}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode
                ? 'Inserat bearbeiten'
                : isMachines
                  ? 'Maschine inserieren'
                  : isPaints
                    ? 'Farbe / Lack / Additiv inserieren'
                    : 'Ersatzteil inserieren'}
            </h1>
            <p className="text-sm text-muted-foreground">{form.status === 'active' ? 'Veröffentlicht' : 'Entwurf'}</p>
          </div>
        </div>

        {!isEditMode && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <Label>Vertical</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={isSpareParts ? 'default' : 'outline'}
                  onClick={() => setForm({ ...form, vertical: 'spare_parts', category_node_id: '' })}
                  data-testid="form-vertical-spare-parts"
                >
                  <Wrench className="w-4 h-4 mr-2" /> Ersatzteil
                </Button>
                <Button
                  type="button"
                  variant={isMachines ? 'default' : 'outline'}
                  onClick={() => setForm({ ...form, vertical: 'machines', category_node_id: '' })}
                  data-testid="form-vertical-machines"
                >
                  <Cog className="w-4 h-4 mr-2" /> Maschine
                </Button>
                <Button
                  type="button"
                  variant={isPaints ? 'default' : 'outline'}
                  onClick={() => setForm({ ...form, vertical: 'paints', category_node_id: '' })}
                  data-testid="form-vertical-paints"
                >
                  <Palette className="w-4 h-4 mr-2" /> Farbe / Lack / Additiv
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Grunddaten</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Titel *</Label>
              <Input id="title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
            </div>
            <div>
              <Label>Kategorie *</Label>
              <Select value={form.category_node_id} onValueChange={v => setForm({ ...form, category_node_id: v })}>
                <SelectTrigger data-testid="form-category"><SelectValue placeholder="Wähle eine Kategorie…" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.node_id} value={c.node_id}>
                      {c.depth > 1 ? '– ' : ''}{c.node_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea rows={4} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Zustand</Label>
                <Select value={form.condition || ''} onValueChange={v => setForm({ ...form, condition: v })}>
                  <SelectTrigger data-testid="form-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preis (€)</Label>
                <Input type="number" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: e.target.value })} data-testid="form-price" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox id="negotiable" checked={!!form.negotiable} onCheckedChange={v => setForm({ ...form, negotiable: !!v })} data-testid="form-negotiable" />
                <Label htmlFor="negotiable">Verhandelbar</Label>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Stadt</Label><Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="form-city" /></div>
              <div><Label>PLZ</Label><Input value={form.postal_code || ''} onChange={e => setForm({ ...form, postal_code: e.target.value })} data-testid="form-postal-code" /></div>
              <div><Label>Land</Label><Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value.toUpperCase() })} data-testid="form-country" /></div>
            </div>
            <Separator />
            <div>
              <Label className="mb-2 block">Sichtbarkeit</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, visibility: 'public' })}
                  className={`flex-1 border rounded-lg p-3 text-left transition-colors ${form.visibility !== 'group_only' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'}`}
                  data-testid="form-visibility-public"
                >
                  <div className="flex items-center gap-2 font-semibold text-sm"><Globe className="w-4 h-4" /> Öffentlich</div>
                  <p className="text-xs text-muted-foreground mt-1">Für alle Käufer auf der Plattform sichtbar.</p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!user?.group_id) {
                      toast.error('Nur-Gruppe erfordert Gruppenmitgliedschaft');
                      return;
                    }
                    setForm({ ...form, visibility: 'group_only' });
                  }}
                  className={`flex-1 border rounded-lg p-3 text-left transition-colors ${form.visibility === 'group_only' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'}`}
                  data-testid="form-visibility-group-only"
                >
                  <div className="flex items-center gap-2 font-semibold text-sm"><Users className="w-4 h-4" /> Nur in meiner Gruppe</div>
                  <p className="text-xs text-muted-foreground mt-1">Nur Mitglieder derselben Unternehmensgruppe sehen dieses Inserat.</p>
                </button>
              </div>
              {!user?.group_id && (
                <p className="text-xs text-muted-foreground mt-2">Hinweis: Du gehörst zu keiner Gruppe, daher ist nur öffentliche Sichtbarkeit verfügbar.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {isSpareParts && (
          <Card className="mt-6">
            <CardHeader><CardTitle>Ersatzteil-Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Teile-Nr.</Label><Input value={form.part_number || ''} onChange={e => setForm({ ...form, part_number: e.target.value })} data-testid="form-part-number" /></div>
                <div><Label>OEM-Nr.</Label><Input value={form.oem_number || ''} onChange={e => setForm({ ...form, oem_number: e.target.value })} data-testid="form-oem-number" /></div>
                <div><Label>Stückzahl</Label><Input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} data-testid="form-quantity" /></div>
                <div>
                  <Label>Einheit</Label>
                  <Select value={form.unit || 'pcs'} onValueChange={v => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="form-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Stück</SelectItem>
                      <SelectItem value="set">Satz</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="m">m</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Kompatible Maschinen</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addCompatibleMachine} data-testid="add-compatible-machine">
                    <Plus className="w-4 h-4 mr-1" /> Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {(form.compatible_machines || []).map((cm, idx) => (
                    <div key={idx} className="flex gap-2 items-end" data-testid={`compatible-row-${idx}`}>
                      <div className="flex-1">
                        <Label className="text-xs">Hersteller</Label>
                        <Select
                          value={cm.manufacturer_id || ''}
                          onValueChange={v => {
                            const mfr = manufacturers.find(m => m.manufacturer_id === v);
                            updateCompatible(idx, { manufacturer_id: v, manufacturer_name: mfr?.name || '' });
                          }}
                        >
                          <SelectTrigger data-testid={`compatible-mfr-${idx}`}><SelectValue placeholder="Hersteller…" /></SelectTrigger>
                          <SelectContent>
                            {manufacturers.map(m => <SelectItem key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Modell</Label>
                        <Input value={cm.model_name || ''} onChange={e => updateCompatible(idx, { model_name: e.target.value })} data-testid={`compatible-model-${idx}`} />
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeCompatible(idx)} data-testid={`remove-compatible-${idx}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {(form.compatible_machines || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">Keine Kompatibilitäts-Tags – füge Hersteller/Modelle hinzu, damit Käufer dein Teil finden.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isPaints && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Farbe / Lack / Additiv – Details</CardTitle>
              <CardDescription>Angaben zu Hersteller, Charge, Menge und Farbcode helfen Käufern beim Finden.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Hersteller</Label>
                  <Select value={form.manufacturer_id || ''} onValueChange={v => {
                    const mfr = manufacturers.find(m => m.manufacturer_id === v);
                    setForm({ ...form, manufacturer_id: v, manufacturer_name: mfr?.name || '' });
                  }}>
                    <SelectTrigger data-testid="form-paints-manufacturer"><SelectValue placeholder="Hersteller wählen…" /></SelectTrigger>
                    <SelectContent>
                      {manufacturers.map(m => <SelectItem key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Artikelnummer</Label>
                  <Input value={form.part_number || ''} onChange={e => setForm({ ...form, part_number: e.target.value })} placeholder="z.B. PR-2451-UV" data-testid="form-paints-article" />
                </div>
                <div>
                  <Label>Farbcode / Pantone</Label>
                  <Input value={form.oem_number || ''} onChange={e => setForm({ ...form, oem_number: e.target.value })} placeholder="z.B. Pantone 185 C / RAL 3020" data-testid="form-paints-color-code" />
                </div>
                <div>
                  <Label>Charge / Lot-Nr. (intern)</Label>
                  <Input value={form.serial_number || ''} onChange={e => setForm({ ...form, serial_number: e.target.value })} placeholder="z.B. CH-2026-0148" data-testid="form-paints-batch" />
                </div>
                <div>
                  <Label>Menge</Label>
                  <Input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} data-testid="form-paints-quantity" />
                </div>
                <div>
                  <Label>Einheit / Gebinde</Label>
                  <Select value={form.unit || 'L'} onValueChange={v => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="form-paints-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Liter</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="bucket">Eimer</SelectItem>
                      <SelectItem value="canister">Kanister</SelectItem>
                      <SelectItem value="drum">Fass</SelectItem>
                      <SelectItem value="ibc">IBC</SelectItem>
                      <SelectItem value="cartridge">Kartusche</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="my-2" />
              <div>
                <h4 className="text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-400 mb-1">REACH &amp; CLP – Pflichtangaben</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Vor dem Veröffentlichen sind Mindesthaltbarkeitsdatum (MHD) und ein gültiges Sicherheitsdatenblatt (SDB) Pflicht (REACH Art. 31 / CLP).
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Mindesthaltbarkeitsdatum (MHD) *</Label>
                  <Input
                    type="date"
                    value={form.best_before_date || ''}
                    onChange={e => setForm({ ...form, best_before_date: e.target.value })}
                    data-testid="form-paints-mhd"
                  />
                </div>
                <div>
                  <Label>UFI-Code (optional)</Label>
                  <Input
                    value={form.ufi_code || ''}
                    onChange={e => setForm({ ...form, ufi_code: e.target.value })}
                    placeholder="z.B. N1QV-R02N-J00M-K10F"
                    data-testid="form-paints-ufi"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Sicherheitsdatenblatt (SDB, PDF) *</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Input
                      type="file"
                      accept="application/pdf"
                      data-testid="form-paints-sdb-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.type !== 'application/pdf') {
                          toast.error('Nur PDF-Dateien sind als Sicherheitsdatenblatt erlaubt.');
                          return;
                        }
                        try {
                          const res = await uploadListingDocument(f, listingId);
                          setForm(prev => ({
                            ...prev,
                            safety_data_sheet_url: res.data.file_url,
                            safety_data_sheet_name: res.data.original_filename,
                          }));
                          toast.success('Sicherheitsdatenblatt hochgeladen');
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Upload fehlgeschlagen');
                        }
                      }}
                    />
                    {form.safety_data_sheet_url && (
                      <a
                        href={form.safety_data_sheet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-fuchsia-700 underline truncate max-w-[200px]"
                        data-testid="form-paints-sdb-link"
                      >
                        {form.safety_data_sheet_name || 'SDB ansehen'}
                      </a>
                    )}
                  </div>
                  {form.safety_data_sheet_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-xs text-red-600"
                      onClick={() => setForm({ ...form, safety_data_sheet_url: '', safety_data_sheet_name: '' })}
                      data-testid="form-paints-sdb-remove"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> SDB entfernen
                    </Button>
                  )}
                </div>
                <div>
                  <Label>Signalwort</Label>
                  <Select value={form.signal_word || 'none'} onValueChange={v => setForm({ ...form, signal_word: v })}>
                    <SelectTrigger data-testid="form-paints-signal-word"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Signalwort</SelectItem>
                      <SelectItem value="warning">Achtung</SelectItem>
                      <SelectItem value="danger">Gefahr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>H-Sätze (Hazard Statements)</Label>
                  <Input
                    value={form.hazard_statements || ''}
                    onChange={e => setForm({ ...form, hazard_statements: e.target.value })}
                    placeholder="z.B. H226, H304, H336"
                    data-testid="form-paints-h-statements"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>GHS-Piktogramme</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['GHS01','GHS02','GHS03','GHS04','GHS05','GHS06','GHS07','GHS08','GHS09'].map(p => {
                      const active = (form.hazard_pictograms || []).includes(p);
                      return (
                        <Button
                          key={p}
                          type="button"
                          size="sm"
                          variant={active ? 'default' : 'outline'}
                          className={active ? 'bg-fuchsia-600 hover:bg-fuchsia-700' : ''}
                          onClick={() => {
                            const set = new Set(form.hazard_pictograms || []);
                            if (set.has(p)) set.delete(p); else set.add(p);
                            setForm({ ...form, hazard_pictograms: Array.from(set) });
                          }}
                          data-testid={`form-paints-pictogram-${p}`}
                        >
                          {p}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Gemäß CLP-Verordnung (z.B. GHS02 – Flamme, GHS07 – Ausrufezeichen).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isMachines && (
          <Card className="mt-6">
            <CardHeader><CardTitle>Maschinen-Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Hersteller</Label>
                  <Select value={form.manufacturer_id || ''} onValueChange={v => {
                    const mfr = manufacturers.find(m => m.manufacturer_id === v);
                    setForm({ ...form, manufacturer_id: v, manufacturer_name: mfr?.name || '' });
                  }}>
                    <SelectTrigger data-testid="form-manufacturer"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>
                      {manufacturers.map(m => <SelectItem key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Modell</Label>
                  <Input
                    value={form.model_name || ''}
                    onChange={e => setForm({ ...form, model_name: e.target.value })}
                    list="model-suggestions"
                    placeholder="z.B. Speedmaster XL 106"
                    data-testid="form-model"
                  />
                  <datalist id="model-suggestions">
                    {models.map(m => <option key={m.model_id} value={m.model_name} />)}
                  </datalist>
                </div>
                <div><Label>Baujahr</Label><Input type="number" value={form.year_of_manufacture} onChange={e => setForm({ ...form, year_of_manufacture: e.target.value })} data-testid="form-year" /></div>
                <div><Label>Seriennummer (intern, nicht öffentlich)</Label><Input value={form.serial_number || ''} onChange={e => setForm({ ...form, serial_number: e.target.value })} data-testid="form-serial" /></div>
                <div><Label>Betriebsstunden</Label><Input type="number" value={form.operating_hours} onChange={e => setForm({ ...form, operating_hours: e.target.value })} data-testid="form-operating-hours" /></div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox id="insp" checked={!!form.inspection_available} onCheckedChange={v => setForm({ ...form, inspection_available: !!v })} data-testid="form-inspection" />
                  <Label htmlFor="insp">Besichtigung möglich</Label>
                </div>
              </div>
              <Separator />
              <p className="text-sm font-semibold">Technische Daten</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Max. Breite (mm)</Label><Input type="number" value={form.specs?.max_width_mm ?? ''} onChange={e => setForm({ ...form, specs: { ...form.specs, max_width_mm: e.target.value } })} data-testid="form-max-width" /></div>
                <div><Label>Anzahl Farben</Label><Input type="number" value={form.specs?.num_colors ?? ''} onChange={e => setForm({ ...form, specs: { ...form.specs, num_colors: e.target.value } })} data-testid="form-num-colors" /></div>
                <div><Label>Max. Geschwindigkeit (m/h)</Label><Input type="number" value={form.specs?.max_speed_mh ?? ''} onChange={e => setForm({ ...form, specs: { ...form.specs, max_speed_mh: e.target.value } })} data-testid="form-max-speed" /></div>
                <div><Label>Leistung (kW)</Label><Input type="number" value={form.specs?.power_kw ?? ''} onChange={e => setForm({ ...form, specs: { ...form.specs, power_kw: e.target.value } })} data-testid="form-power" /></div>
                <div><Label>Gewicht (kg)</Label><Input type="number" value={form.specs?.weight_kg ?? ''} onChange={e => setForm({ ...form, specs: { ...form.specs, weight_kg: e.target.value } })} data-testid="form-weight" /></div>
                <div><Label>Abmessungen L x B x H</Label><Input value={form.specs?.dimensions_lwh_mm || ''} onChange={e => setForm({ ...form, specs: { ...form.specs, dimensions_lwh_mm: e.target.value } })} data-testid="form-dimensions" /></div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos / Media */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Fotos & Medien</CardTitle>
            <CardDescription>Lade Fotos direkt per Drag & Drop hoch (JPG/PNG/WebP, max. 10MB). Video-Links: YouTube/Vimeo.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Fotos</Label>
              <ListingPhotoUploader
                photos={form.photos || []}
                onChange={(next) => setForm({ ...form, photos: next })}
                listingId={isEditMode ? listingId : null}
                maxPhotos={10}
              />
            </div>
            <div>
              <Label>Video-Link hinzufügen</Label>
              <div className="flex gap-2">
                <Input placeholder="https://youtube.com/…" value={videoInput} onChange={e => setVideoInput(e.target.value)} data-testid="form-video-input" />
                <Button type="button" onClick={() => {
                  if (!videoInput.trim()) return;
                  setForm({ ...form, video_links: [...(form.video_links || []), videoInput.trim()] });
                  setVideoInput('');
                }} data-testid="form-add-video">Hinzufügen</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(form.video_links || []).map((v, i) => (
                  <Badge key={i} variant="secondary" className="gap-1" data-testid={`form-video-${i}`}>
                    {v.slice(0, 30)}…
                    <button type="button" onClick={() => setForm({ ...form, video_links: form.video_links.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-btn">
            <Save className="w-4 h-4 mr-2" /> Als Entwurf speichern
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} data-testid="publish-btn">
            <Send className="w-4 h-4 mr-2" /> Veröffentlichen
          </Button>
        </div>
      </main>
    </div>
  );
}
